import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Divider, Button, Empty, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import _ from 'lodash';

import { FormValue } from '../useForm';
import { MACHINING_CODE_CONDITION } from 'src/constants/quotation/optionSelect';
import useProductMachiningMultiple from 'src/queries/useProductMachiningMultiple';
import Modal from 'src/components/custom/Modal';
import { ChamferModal } from 'src/components/common';
import { SCREENS } from 'src/constants/screens';
import {
  getProductName,
  checkIsProductFinished,
  checkChangeLanguage,
} from 'src/utils/helper';
import { SelectField } from 'src/components/form';
import { BLANK_OPTION, TYPE_NAME_INPUT } from 'src/constants/app';

type Props = {
  index: number;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate: Response.QuotationItem;
  prevLang: string;
  lang: string;
};

const MachiningMethodList: React.FC<Props> = ({
  index,
  formInfo,
  dataReEstimate,
  prevLang,
  lang,
}) => {
  const [visibleChamferPopup, setVisibleChamferPopup] = useState(false);
  const [shouldFetchMachinings, setShouldFetchMachinings] = useState(false);
  const [chamferMethod, setChamferMethod] =
    useState<Response.ChamferItem | null>();
  const { t } = useTranslation();
  const { setValue, getValues, control, watch } = formInfo;
  const prevProductRef = useRef(dataReEstimate?.product);

  const productCode = watch(`priceQuotes.${index}.product`);
  const varietyCode = watch(`priceQuotes.${index}.variety`);
  const shapeCode = watch(`priceQuotes.${index}.shape`);
  const [noData, setNodata] = useState('');
  const [machinings, setMachiningsData] = useState<any>();

  const isResetByProductSelection = watch(
    `priceQuotes.${index}.isResetByProductSelection`,
  );

  // Conditionally pass params to control API call
  const apiParams = shouldFetchMachinings
    ? {
        product: productCode,
        hinshL3Cd: varietyCode,
        keijoL3Cd: shapeCode,
      }
    : null;
  const { isLoading } = useProductMachiningMultiple(
    apiParams,
    setMachiningsData,
  );

  // Track when dataReEstimate?.product changes
  useEffect(() => {
    const currentProduct = dataReEstimate?.product;
    const prevProduct = prevProductRef.current;

    if (currentProduct !== prevProduct && currentProduct) {
      setShouldFetchMachinings(true);
    }

    prevProductRef.current = currentProduct;
  }, [dataReEstimate?.product]);

  // Set initial value when dataReEstimate?.setdnKakoCd exists
  useEffect(() => {
    if (!shouldFetchMachinings) {
      const value = dataReEstimate?.setdnKakoCd || '';
      setMachiningItem(value);
      checkVisibleChamferPopup(value);
    }
    if (dataReEstimate?.fraiseKakoPtnm) {
      onSelectChamferMethod({
        fraiseKakoPtnMeis: dataReEstimate?.fraiseKakoPtnm,
        fraiseKakoPtn: dataReEstimate?.fraiseKakoPtn,
        setdnKakoCd: dataReEstimate?.setdnKakoCd,
      });
    }
  }, [dataReEstimate?.setdnKakoCd]);

  useEffect(() => {
    if (
      !getValues(`priceQuotes.${index}.machiningItem`)?.value &&
      machinings?.methods?.length === 1
    ) {
      const machiningMethodFirst = _.get(machinings.methods, '[0]');
      checkVisibleChamferPopup(machiningMethodFirst?.value);
      setValue(`priceQuotes.${index}.machiningItem`, machiningMethodFirst, {
        shouldValidate: false,
      });
      return;
    }
    if (isResetByProductSelection) {
      setMachiningItem('');
      checkVisibleChamferPopup('');
    }
  }, [isResetByProductSelection, machinings?.methods]);

  useEffect(() => {
    if (checkChangeLanguage(prevLang, lang)) {
      setMachiningsData(null);
    }
  }, [lang]);

  const _onChangeMachining = (value) => {
    // User changes machining method => validate to clear any existing errors
    setMachiningItem(value, { validate: true });
    // reset chamfer method value
    setChamferMethod(null);
    setValue(`priceQuotes.${index}.chamferItem`, {});
    checkVisibleChamferPopup(value);
    setValue(`priceQuotes.${index}.isResetByMachiningMethod`, true);
    setValue(
      `priceQuotes.${index}.inputAutoFillByType`,
      TYPE_NAME_INPUT.PRODUCT_THICKNESS,
    );
  };

  const checkVisibleChamferPopup = (value) => {
    if (MACHINING_CODE_CONDITION.includes(value)) {
      setVisibleChamferPopup(true);
    } else {
      setVisibleChamferPopup(false);
    }
  };

  // Set machining item by id (and optionally trigger validation)
  const setMachiningItem = (value, options: { validate?: boolean } = {}) => {
    const shouldValidate = !!options.validate;

    // First try to find in machinings methods
    let machiningItem = machinings?.methods?.find(
      (item) => item.value == value,
    );

    // If not found and we have dataReEstimate data, create item from it
    if (!machiningItem && dataReEstimate?.setdnKakoCd === value) {
      machiningItem = {
        value: dataReEstimate?.setdnKakoCd,
        label: dataReEstimate?.setdnkako,
        description: [],
      };
    }

    if (machiningItem && !checkIsProductFinished(varietyCode)) {
      if (machinings?.methods?.length === 0 && !machiningItem?.value) {
        setValue(`priceQuotes.${index}.machiningItem`, undefined);
        setNodata(t('pricing_page.empty_data_message'));
      } else {
        setValue(`priceQuotes.${index}.machiningItem`, machiningItem, {
          shouldValidate,
        });
      }
    }
  };

  // Handle select focus to enable API call
  const handleSelectFocus = () => {
    if (dataReEstimate?.product) {
      setShouldFetchMachinings(true);
    }
  };

  // Select chamfer method and set chamfer item to form
  const onSelectChamferMethod = (value) => {
    setChamferMethod(value);
    setValue(`priceQuotes.${index}.chamferItem`, value || {});
    // User selects chamfer => also validate machiningItem
    setMachiningItem(value?.setdnKakoCd, { validate: true });
  };

  return (
    <Row className="machining_row">
      <Col
        xs={24}
        sm={24}
        md={24}
        xl={24}
        className="machining_col machining-item__select"
      >
        <Tooltip
          title={
            control?._formValues?.priceQuotes?.[index]?.machiningItem?.label ||
            (!isLoading && machinings?.methods?.length === 0 ? noData : '')
          }
          placement="top"
          overlayClassName="custom-tooltip"
        >
          <SelectField<FormValue>
            className="machining_item_select"
            showErrorTooltip
            controller={{ name: `priceQuotes.${index}.machiningItem`, control }}
            onFocus={handleSelectFocus}
            selectProps={{
              options: [BLANK_OPTION, ...(machinings?.methods || [])],
              placeholder:
                !isLoading && machinings?.methods?.length === 0 ? noData : '',
              notFoundContent: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('pricing_page.category_field.no_data')}
                />
              ),
              showSearch: true,
              filterOption: (inputValue, option) => {
                const label = option?.label?.toString().toUpperCase() || '';
                return label.includes(inputValue.toUpperCase());
              },
              onSelect: _onChangeMachining,
              disabled:
                !dataReEstimate?.product || checkIsProductFinished(varietyCode),
            }}
          />
        </Tooltip>
      </Col>
      {visibleChamferPopup && (
        <Col span={24} className="machining_handle machining_col">
          <Divider className="mg-vertical-10" />
          <Modal
            modalProps={{ width: 840 }}
            title={t('chamfer_modal.title')}
            content={({ onClose }) => (
              <ChamferModal
                screenId={SCREENS.R2004}
                onSelect={onSelectChamferMethod}
                productName={getProductName(
                  getValues(`priceQuotes.${index}.productItem`),
                  true,
                )}
                processingMethod={
                  getValues(`priceQuotes.${index}.machiningItem`)?.label
                }
                onClose={onClose}
              />
            )}
          >
            <span>
              {chamferMethod && (
                <span className="chamfer_method">
                  ({chamferMethod?.fraiseKakoPtnMeis}){' '}
                </span>
              )}
              <Button className="machining_handle_button" type="primary">
                {t('pricing_page.machining_method_button')}
              </Button>
            </span>
          </Modal>
        </Col>
      )}
    </Row>
  );
};

export default MachiningMethodList;
